import { useState } from "react";
import PdmCard from "../../components/PdmCard";
import Icon from "../../components/ui/Icon";
import { DEVICE_TYPES } from "../../domain/models";

export default function DevicesView({
  devices,
  nodes = [],
  onToggle,
  onDim,
  onConfigure,
  onOpenBms,
  onCreatePdm,
}) {
  const [deviceFilters, setDeviceFilters] = useState(["All"]);
  const filterOptions = ["All", "PDM", "Battery", "Sensors", "VE.Direct"];

  const toggleFilter = (id) => {
    if (id === "All") {
      setDeviceFilters(["All"]);
      return;
    }
    setDeviceFilters((prev) => {
      const withoutAll = prev.filter((f) => f !== "All");
      if (withoutAll.includes(id)) {
        const next = withoutAll.filter((f) => f !== id);
        return next.length === 0 ? ["All"] : next;
      }
      return [...withoutAll, id];
    });
  };

  const showAll = deviceFilters.includes("All");
  const showPDM = showAll || deviceFilters.includes("PDM");
  const showBMS = showAll || deviceFilters.includes("Battery");
  const showSensors = showAll || deviceFilters.includes("Sensors");
  const showVED = showAll || deviceFilters.includes("VE.Direct");

  const hasEwlNodes = nodes.some((n) => n.subtype === "EWL-810");
  const bmsBridgeNode = nodes.find((n) => n.type === "BMS") ?? null;
  const hasFreeEwlSlots = nodes.some(
    (n) => n.subtype === "EWL-810" && (n.channels || []).some((ch) => !ch.ownerDeviceId)
  );

  const pdmDevices = devices.filter(
    (d) => d.type === DEVICE_TYPES.PDM_CHANNEL && d.showOnDevices !== false
  );
  const bmsDevices = devices.filter((d) => d.type === DEVICE_TYPES.BMS);
  const levelSensors = devices.filter((d) => d.type === DEVICE_TYPES.SENSOR_LEVEL);
  const tempSensors = devices.filter((d) => d.type === DEVICE_TYPES.SENSOR_TEMPERATURE);
  const veDirectDevices = devices.filter((d) => d.type === DEVICE_TYPES.VE_DIRECT);

  return (
    <div className="space-y-5 pt-2">
      {/* Search + Filters */}
      <div className="space-y-3">
        <div className="w-full rounded-2xl border border-brand-navy bg-brand-purple px-4 py-1.5 flex items-center gap-2 text-sm text-brand-light">
          <Icon name="search" className="text-lg text-brand-light/40" />
          <input
            type="text"
            placeholder="Search devices..."
            className="flex-1 bg-transparent placeholder:text-brand-light/40 text-xs focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filterOptions.map((id) => {
            const active = deviceFilters.includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleFilter(id)}
                className={`rounded-full px-3 py-1.5 text-[11px] font-semibold border transition ${
                  active
                    ? "bg-brand-magenta border-brand-magenta text-white shadow-sm"
                    : "bg-brand-purple border-brand-purple/70 text-brand-light hover:border-brand-light/40"
                }`}
              >
                {id}
              </button>
            );
          })}
        </div>
      </div>

      {/* "Add devices" card when no devices but EWL nodes exist */}
      {devices.length === 0 && hasEwlNodes && (
        <div className="rounded-3xl border border-brand-navy bg-brand-navy px-4 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-brand-light">
              <Icon name="add" />
              <span>Add devices</span>
            </div>
            <span className="text-[11px] text-brand-light/40">EWL detected</span>
          </div>
          <p className="text-xs text-brand-light/70">
            No devices yet. Create channels from the connected EWL nodes.
          </p>
          <button
            type="button"
            onClick={onCreatePdm}
            className="w-full rounded-xl bg-brand-magenta px-4 py-2 text-sm font-semibold text-white hover:bg-brand-pink transition"
          >
            Add a channel
          </button>
        </div>
      )}

      {/* PDM Channels */}
      {showPDM && pdmDevices.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black italic text-brand-light">PDM Channels</h2>
            <div className="flex items-center gap-2">
              {onCreatePdm && hasFreeEwlSlots ? (
                <button
                  type="button"
                  onClick={onCreatePdm}
                  className="rounded-lg bg-brand-magenta px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-pink transition"
                >
                  New channel
                </button>
              ) : hasEwlNodes ? (
                <span className="text-[11px] text-brand-light/40">No free slots</span>
              ) : null}
              <span className="text-xs text-brand-light/40">{pdmDevices.length} channels</span>
            </div>
          </div>
          <div className="space-y-2">
            {pdmDevices.map((device) => (
              <PdmCard
                key={device.id}
                device={device}
                onToggle={onToggle}
                onDimChange={onDim}
                onCardPress={onConfigure}
              />
            ))}
          </div>
        </section>
      )}

      {/* Battery */}
      {showBMS && bmsDevices.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black italic text-brand-light">Battery</h2>
            <span className="text-xs text-brand-light/40">
              {bmsDevices.length} device{bmsDevices.length !== 1 ? "s" : ""}
            </span>
          </div>
          {bmsDevices.map((bms) => (
            <button
              key={bms.id}
              type="button"
              onClick={() => onOpenBms && onOpenBms(bms.id)}
              className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-3 flex items-center gap-4 w-full text-left hover:border-brand-magenta/70 transition"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-purple border border-brand-navy text-xl">
                <Icon name="battery_charging_full" className="text-brand-magenta" />
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-brand-light">{bms.name}</span>
                    <span className="text-[11px] text-emerald-400">
                      {bms.metrics?.statusLine || "Charging"} | {bms.metrics?.voltage ?? "--"} V
                    </span>
                    {bmsBridgeNode && (
                      <span className="text-[10px] text-brand-light/40 mt-0.5">
                        via {bmsBridgeNode.name}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-2xl font-semibold text-brand-magenta">
                      {bms.metrics?.soc ?? "--"}%
                    </span>
                    <span className="text-[11px] text-brand-light/50">
                      {bms.metrics?.timeLeft || ""}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-[11px] text-brand-light pt-1">
                  <div className="flex items-center gap-1">
                    <span>☀️</span>
                    <span>120 W</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>⇄</span>
                    <span>-80 W</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="device_thermostat" />
                    <span>21°C</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </section>
      )}

      {/* Level Sensors */}
      {showSensors && levelSensors.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black italic text-brand-light">Level Sensors</h2>
            <span className="text-xs text-brand-light/40">{levelSensors.length} sensors</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {levelSensors.map((sensor) => {
              const isFresh = sensor.id === "sensor-fresh" || sensor.name.toLowerCase().includes("fresh");
              const barColor = isFresh ? "bg-sky-400" : "bg-amber-400";
              const emoji = isFresh ? "🌊" : null;
              return (
                <div
                  key={sensor.id}
                  className="rounded-3xl border border-brand-navy bg-brand-purple px-3 py-3 flex flex-col gap-2 text-left"
                >
                  <div className="flex items-center gap-2">
                    {emoji ? (
                      <span className="text-base">{emoji}</span>
                    ) : (
                      <Icon name="water_drop" className="text-base text-brand-light" />
                    )}
                    <span className="text-xs font-medium text-brand-light">{sensor.name}</span>
                  </div>
                  <div className="mt-1">
                    <div className="relative h-1.5 rounded-full bg-brand-navy overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 ${barColor}`}
                        style={{ width: `${sensor.value ?? 0}%` }}
                      />
                    </div>
                    <span className="mt-1 block text-[11px] text-brand-light/70">
                      {sensor.value ?? "--"}% ({sensor.volume ?? "--"}{sensor.volumeUnit || "L"})
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Temperature Sensors */}
      {showSensors && tempSensors.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black italic text-brand-light">Temperature Sensors</h2>
            <span className="text-xs text-brand-light/40">{tempSensors.length} sensors</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {tempSensors.map((sensor) => {
              const isEmoji = sensor.icon && !sensor.icon.match(/^[a-z_]/);
              return (
                <div
                  key={sensor.id}
                  className="rounded-3xl border border-brand-navy bg-brand-purple px-3 py-3 flex flex-col gap-1 text-left"
                >
                  <div className="flex items-center gap-2">
                    {isEmoji ? (
                      <span className="text-base">{sensor.icon}</span>
                    ) : (
                      <Icon name={sensor.icon || "device_thermostat"} className="text-base text-brand-light" />
                    )}
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-brand-light">{sensor.name}</span>
                      {sensor.sublabel && (
                        <span className="text-[11px] text-brand-light/50">{sensor.sublabel}</span>
                      )}
                    </div>
                  </div>
                  <span className="mt-1 text-lg font-semibold text-brand-light">
                    {sensor.value ?? "--"}°C
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* VE.Direct */}
      {showVED && veDirectDevices.length > 0 && (
        <section className="space-y-3 pb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black italic text-brand-light">VE.Direct</h2>
            <span className="text-xs text-brand-light/40">{veDirectDevices.length} device{veDirectDevices.length !== 1 ? "s" : ""}</span>
          </div>
          {veDirectDevices.map((device) => (
            <div
              key={device.id}
              className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-3 flex items-center gap-3"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-purple border border-brand-navy text-lg">
                <Icon name="wb_sunny" className="text-sky-400" />
              </div>
              <div className="flex flex-col flex-1">
                <span className="text-sm font-medium text-brand-light">{device.name}</span>
                <span className="text-[11px] text-sky-300 mt-0.5">{device.status}</span>
              </div>
              <div className="flex flex-col items-end text-right">
                <span className="text-sm font-semibold text-sky-400">
                  {device.metrics?.power ?? "--"} W
                </span>
                <span className="text-[10px] text-brand-light/50">Output</span>
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
